import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import { useAuth } from './useAuth'
import { dedupeRefRows } from '../lib/dedupeRefs'

// ── FETCH SERMONS ─────────────────────────────────────────────
// Pass `churchIds` (an array) to scope the query to just those churches — the
// reader passes the user's followed + home churches so it never downloads the
// whole platform's sermons (essential at 100s of churches). Behaviour:
//   • churchIds = undefined  → legacy "load all" (RLS still applies); for
//                              callers that haven't been migrated.
//   • churchIds = []         → load nothing (e.g. a user following no church).
//   • churchIds = [ids...]   → only those churches' sermons.
// The query refetches whenever the id set changes (e.g. the user follows a new
// church), so newly-followed churches' dots appear without a manual refresh.
const SERMON_COLS = 'id, church_id, uploaded_by, title, display_title, series, playlists, preacher, date, youtube_id, youtube_url, duration, created_at, updated_at, sermon_refs(*), profiles(full_name)'
const SERMON_COLS_NO_PLAYLISTS = 'id, church_id, uploaded_by, title, display_title, series, preacher, date, youtube_id, youtube_url, duration, created_at, updated_at, sermon_refs(*), profiles(full_name)'
// Tolerates display_title not existing yet either (migration not run) — the
// safe minimal common denominator, excluding both newer columns.
const SERMON_COLS_NO_DISPLAY_TITLE = 'id, church_id, uploaded_by, title, series, preacher, date, youtube_id, youtube_url, duration, created_at, updated_at, sermon_refs(*), profiles(full_name)'

export function useSermons(churchIds) {
  const scoped = Array.isArray(churchIds)
  const ids = scoped ? [...new Set(churchIds.filter(Boolean))] : null
  // Stable dependency key so we only refetch when the set actually changes.
  const idsKey = scoped ? ids.slice().sort().join(',') : '__ALL__'

  const [sermons, setSermons] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    // Scoped-but-empty → nothing to load (user follows no church).
    if (idsKey !== '__ALL__' && idsKey === '') { setSermons([]); setLoading(false); return }
    const list = idsKey === '__ALL__' ? null : idsKey.split(',')

    // Explicit column list — deliberately omits `transcript` (a large per-sermon
    // blob the list/dots never render). Selecting it via `*` ballooned the
    // payload to megabytes. Load it lazily only when actually needed.
    const build = (cols) => {
      let q = supabase.from('sermons').select(cols)
      if (list) q = q.in('church_id', list)
      return q.order('date', { ascending: false })
    }
    let { data } = await build(SERMON_COLS)
    if (!data) { data = (await build(SERMON_COLS_NO_PLAYLISTS)).data }  // tolerate missing `playlists` column
    if (!data) { data = (await build(SERMON_COLS_NO_DISPLAY_TITLE)).data }  // tolerate missing `display_title` column too
    setSermons(data || [])
    setLoading(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey])

  useEffect(() => { fetch() }, [fetch])

  return { sermons, loading, refresh: fetch }
}

// ── SERMON REFS FOR A SPECIFIC VERSE ─────────────────────────
export function useVerseSermonRefs(verseRef) {
  const [refs, setRefs] = useState([])

  useEffect(() => {
    if (!verseRef) return
    supabase
      .from('sermon_refs_full')
      .select('*')
      .eq('verse_ref', verseRef)
      .order('sermon_date', { ascending: false })
      .then(({ data }) => setRefs(data || []))
  }, [verseRef])

  return refs
}

// ── ADMIN: SAVE A SERMON + REFS ───────────────────────────────
export function useSermonAdmin() {
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState(null)

  // Save a full sermon with its scripture references. Persists church_id on
  // both rows so RLS can scope without needing to join through the parent.
  async function saveSermon(sermonData, refs) {
    setSaving(true); setError(null)
    try {
      // 1. Upsert the sermon. `source` marks this as an in-app self-serve
      //    upload (vs 'admin_tool' from the bulk importer).
      const sermonRow = {
        id:           sermonData.id || undefined,
        uploaded_by:  user.id,
        church_id:    sermonData.churchId,
        title:        sermonData.title,
        series:       sermonData.series,
        preacher:     sermonData.preacher,
        date:         sermonData.date,
        youtube_id:   sermonData.youtubeId,
        youtube_url:  sermonData.youtubeUrl,
        duration:     sermonData.duration,
        transcript:   sermonData.transcript,
        source:       'in_app',
        updated_at:   new Date().toISOString(),
        // Optional AI summary + display title (only set when provided);
        // tolerated below if the columns don't exist yet.
        ...(sermonData.aiSummary != null ? { ai_summary: sermonData.aiSummary } : {}),
        ...(sermonData.displayTitle != null ? { display_title: sermonData.displayTitle } : {}),
      }
      let { data: sermon, error: sErr } = await supabase
        .from('sermons').upsert(sermonRow).select().single()
      // Tolerate the source / ai_summary / display_title columns not existing
      // yet (migration not run) — strip whichever the error names and retry.
      if (sErr && /column.*(source|ai_summary|display_title).*does not exist/i.test(sErr.message || '')) {
        // eslint-disable-next-line no-unused-vars
        const { source, ai_summary, display_title, ...rest } = sermonRow
        ;({ data: sermon, error: sErr } = await supabase
          .from('sermons').upsert(rest).select().single())
      }

      if (sErr) throw sErr

      // 2. Always clear existing refs for this sermon before re-inserting, so a
      // re-scan / edit REPLACES its refs instead of doubling them. For a brand-
      // new sermon this is a harmless no-op (no refs exist yet).
      await supabase.from('sermon_refs').delete().eq('sermon_id', sermon.id)

      // 3. Insert new refs. Also write church_id so direct RLS scoping works.
      // If the column doesn't exist yet (migration not applied), retry without.
      if (refs.length > 0) {
        const baseRows = dedupeRefRows(refs.map(r => ({
          sermon_id:   sermon.id,
          verse_ref:   r.verseRef,
          book:        r.book,
          chapter:     r.chapter,
          verse:       r.verse,
          // Prefer the new snake_case `timestamp_s` Claude is asked to return.
          // Fall back to the older `timestampS` field for any cached / legacy
          // ref objects still flowing through.
          timestamp_s: r.timestamp_s ?? r.timestampS ?? null,
          timestamp:   r.timestamp,
          notes:       r.notes,
          // main_passage | moment | cross_reference — defaults to 'moment'
          // (the DB column's own default) for any ref Claude didn't tag.
          kind:        r.kind || 'moment',
        })))
        let rErr, hasChurchId = true
        const buildRows = (withKind) => baseRows.map(row => {
          const r2 = withKind ? row : (() => { const { kind: _k, ...rest } = row; return rest })()
          return hasChurchId ? { ...r2, church_id: sermonData.churchId } : r2
        })
        ;({ error: rErr } = await supabase.from('sermon_refs').insert(buildRows(true)))
        if (rErr && /column.*church_id.*does not exist/i.test(rErr.message || '')) {
          console.warn('sermon_refs.church_id missing - retrying without it.')
          hasChurchId = false
          ;({ error: rErr } = await supabase.from('sermon_refs').insert(buildRows(true)))
        }
        if (rErr && /column.*kind.*does not exist/i.test(rErr.message || '')) {
          console.warn('sermon_refs.kind missing - retrying without it.')
          ;({ error: rErr } = await supabase.from('sermon_refs').insert(buildRows(false)))
        }
        if (rErr) throw rErr
      }

      setSaving(false)
      return { sermon, error: null }
    } catch (err) {
      setError(err.message)
      setSaving(false)
      return { sermon: null, error: err }
    }
  }

  async function deleteSermon(id) {
    return supabase.from('sermons').delete().eq('id', id)
  }

  return { saveSermon, deleteSermon, saving, error }
}

// Reconstruct a "[MM:SS] text" transcript view from a ScrapeCreators-style
// segment array. ScrapeCreators returns `startMs` in milliseconds — usually
// as a number, occasionally as a string ("0", "5000", …), so we coerce.
// `start_ms` and `start` (seconds) are accepted as fallbacks.
export function formatTimestampedTranscript(segs) {
  if (!Array.isArray(segs) || segs.length === 0) return ''
  const lines = []
  for (const seg of segs) {
    const text = String(seg?.text ?? '').trim()
    if (!text) continue
    let startMs = null
    if (seg.startMs != null) {
      const n = typeof seg.startMs === 'number' ? seg.startMs : parseInt(seg.startMs, 10)
      if (Number.isFinite(n)) startMs = n
    }
    if (startMs == null && seg.start_ms != null) {
      const n = typeof seg.start_ms === 'number' ? seg.start_ms : parseInt(seg.start_ms, 10)
      if (Number.isFinite(n)) startMs = n
    }
    if (startMs == null && seg.start != null) {
      const n = typeof seg.start === 'number' ? seg.start : parseFloat(seg.start)
      if (Number.isFinite(n)) startMs = Math.round(n * 1000)
    }
    if (startMs == null) { lines.push(text); continue }
    const minutes = Math.floor(startMs / 60000)
    const seconds = Math.floor((startMs % 60000) / 1000)
    const time = `${minutes}:${seconds.toString().padStart(2, '0')}`
    lines.push(`[${time}] ${text}`)
  }
  return lines.join('\n')
}

// Shared system prompt — used by both SermonUpload's scan and AdminPanel's
// bulk import. Keeping a single source of truth so behaviour can't drift.
export const SCAN_SYSTEM_PROMPT = `You are a Bible reference extractor. You ONLY output valid JSON arrays. Never output any other text, explanation, or commentary under any circumstances. Your entire response must be a JSON array starting with [ and ending with ]. Nothing else.

Extract all Bible verse references from the transcript below.
For each reference found return an object with these fields:
- verse_ref: full reference e.g. "Romans 8:1" or "Romans 6:1-14" for a range
- book: book name e.g. "Romans"
- chapter: chapter number as integer
- verse: first verse of the reference as integer
- end_verse: last verse of the reference as integer. For a single verse like "Romans 8:1" set end_verse equal to verse. For a range like "Romans 6:1-14" set verse=1 and end_verse=14.
- timestamp_s: seconds from start of video where this verse was mentioned. The transcript has [MM:SS] marks at the start of each line — use the nearest preceding mark on the same line as the reference and convert to total seconds (minutes * 60 + seconds). Return as an integer. The only legitimate 0 is a reference at the very start of the transcript.
- notes: 1-2 sentences describing what the preacher said about this specific verse — the point they were making, the application or insight given. Written in third person, e.g. "The preacher used this verse to…".
- kind: one of "main_passage", "moment", or "cross_reference".
  - "main_passage": this reference is (part of) the sermon's own main text — the passage the whole sermon is built on and works through.
  - "moment": a verse quoted, read, or referenced briefly — not the sermon's main text.
  - "cross_reference": a verse from a different book or chapter than the sermon's main passage, brought in to support a point.
  Most sermons have one main passage (which may need several objects if the preacher returns to different parts of it at different timestamps — give each the same kind "main_passage") and several moments/cross-references around it.

IMPORTANT RULE FOR VERSE RANGES:
When a preacher references a passage range like Romans 6:1-14 or Ephesians 6:10-18, return ONE object for the whole range. Do NOT split ranges into individual verse objects.
For ranges set:
- verse_ref: the full range, e.g. "Romans 6:1-14"
- book: "Romans"
- chapter: 6
- verse: 1 (first verse only)
- end_verse: 14 (last verse of range)
Only create separate objects when the preacher genuinely references separate verses at different points in the sermon (e.g. they read Romans 6:1-14 at 12:30 and then come back to Romans 6:23 alone at 28:45 — that's two objects).

Two special cases that MUST be reflected in the notes when they apply:
- If the verse was brought to the meeting by a congregation member, guest, or someone other than the preacher (e.g. as a word of encouragement, prophetic word, scripture shared from the floor), begin the note with phrasing such as "A congregation member brought…" / "A guest shared…" / "Someone in the congregation shared a word of encouragement using…". Do not call them "the preacher" in that case.
- If the verse is being used in the context of communion / the Lord's Supper / breaking of bread / the cup of remembrance, mention "communion" or "the Lord's Supper" explicitly in the notes so this can be filtered out of sermon references.

If no Bible verses are found return an empty array: []`

// ── SCAN TRANSCRIPT WITH CLAUDE ───────────────────────────────
// Sends the transcript to Claude (via the claude-proxy edge function) and
// gets back structured scripture refs. When `transcriptJson` (timestamped
// segment array from ScrapeCreators) is provided, we reconstruct a
// "[MM:SS] text" view so Claude can pin each verse ref to an exact timestamp.
// Falls back to plain text alone when no array is available.
//
// Uses supabase.functions.invoke so the user's auth token is attached
// automatically — fixes the non-2xx CORS / auth issue from the old raw
// fetch path.
export async function scanTranscript(transcript, sermonMeta, transcriptJson, opts = {}) {
  const onLog = typeof opts.onLog === 'function' ? opts.onLog : () => {}
  const timestamped = formatTimestampedTranscript(transcriptJson)
  const transcriptForClaude = timestamped || transcript

  // No captions on YouTube → nothing to scan. Bail out BEFORE calling Claude
  // (an empty message returns a non-2xx). Return an empty array tagged so
  // callers can flag the video as "no captions" rather than treating it as a
  // hard error or an empty-but-successful scan.
  if (!transcriptForClaude || !String(transcriptForClaude).trim()) {
    onLog('No captions/transcript available on YouTube — skipping scan.', 'warn')
    const empty = []
    empty.noTranscript = true
    return empty
  }

  // Standard tail appended to every user message so Claude can't miss the
  // JSON-only contract even if the system prompt is somehow stripped.
  const JSON_ONLY_TAIL =
    `\n\nRespond with a JSON array only. Start your response with [ immediately. ` +
    `No explanation. No preamble. No text after the closing ]. Just the JSON array.`

  // Single Claude call returning the cleaned response text or throwing on
  // hard errors (network / auth). Strips markdown code fences too.
  async function callClaude(userMessage) {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 4000,
        system:     SCAN_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userMessage }],
      },
    })
    if (error) {
      try {
        const props = {}
        for (const k of Object.keys(error)) props[k] = error[k]
        // eslint-disable-next-line no-console
        console.error('[scanTranscript] supabase.functions.invoke error:', {
          name:    error?.name,
          message: error?.message,
          status:  error?.status ?? error?.context?.status,
          context: error?.context,
          cause:   error?.cause,
          ...props,
          responseBody: data,
        })
      } catch (_ignore) {
        // eslint-disable-next-line no-console
        console.error('[scanTranscript] error (no enumerable props):', error)
      }
      const upstream = data?.error?.message || data?.message || ''
      throw new Error(upstream || error.message || 'claude-proxy failed')
    }
    if (data?.error) throw new Error(data.error.message || JSON.stringify(data.error))
    const text = data?.content?.[0]?.text || ''
    if (!text) throw new Error('Empty response from Claude')
    return text.replace(/^```[a-z]*\n?/i, '').replace(/\n?```$/i, '').trim()
  }

  // Claude is now asked for snake_case `verse_ref`, but several downstream
  // consumers read camelCase `verseRef`. Mirror the field both ways so old
  // and new callers both work.
  // Pull the end-of-range verse out of a verseRef string like
  // "Ephesians 6:8-13" → 13. Single-verse refs and chapter-only refs
  // return null so the caller can decide what fallback to use.
  function parseEndVerse(verseRef) {
    const m = String(verseRef || '').match(/(\d+):(\d+)(?:-(\d+))?/)
    if (!m) return null
    return m[3] ? parseInt(m[3], 10) : parseInt(m[2], 10)
  }

  function normalize(arr) {
    if (!Array.isArray(arr)) return arr
    for (const r of arr) {
      if (!r || typeof r !== 'object') continue
      if (r.verse_ref != null && r.verseRef == null) r.verseRef = r.verse_ref
      if (r.verseRef != null && r.verse_ref == null) r.verse_ref = r.verseRef
      // Compute end_verse from the verse_ref string when Claude didn't
      // supply it. Mirror to camelCase too for callers reading either
      // shape. Ranges like "Ephesians 6:8-13" become {verse:8, end_verse:13};
      // singletons become {verse:N, end_verse:N}.
      if (r.end_verse == null && r.endVerse == null) {
        const ev = parseEndVerse(r.verse_ref || r.verseRef)
        if (ev != null) { r.end_verse = ev; r.endVerse = ev }
      } else if (r.end_verse == null) {
        r.end_verse = r.endVerse
      } else if (r.endVerse == null) {
        r.endVerse = r.end_verse
      }
    }
    return arr
  }

  // First attempt — base user message + JSON-only tail.
  const baseUserMessage =
    `Sermon: "${sermonMeta.title}" | Series: "${sermonMeta.series || 'N/A'}" | Date: ${sermonMeta.date}\n\nTranscript:\n${transcriptForClaude}`
    + JSON_ONLY_TAIL

  let clean = await callClaude(baseUserMessage)
  if (clean[0] === '[' || clean[0] === '{') {
    try { return normalize(JSON.parse(clean)) } catch (parseErr) {
      // Looked like JSON but didn't parse — don't retry per spec; surface
      // as a non-fatal 0-refs result.
      // eslint-disable-next-line no-console
      console.warn('[scanTranscript] JSON.parse failed, treating as 0 refs:', parseErr.message, 'Preview:', clean.slice(0, 200))
      const empty = []
      empty.nonJson = true
      empty.rawPreview = clean.slice(0, 200)
      empty.parseError = parseErr.message
      return empty
    }
  }

  // Non-JSON response — log to UI, wait 5s, retry once with stronger nudge.
  onLog('⚠ Claude returned non-JSON — retrying...', 'warn')
  onLog(`Claude said: ${clean.slice(0, 200)}`, 'orange')
  // eslint-disable-next-line no-console
  console.warn('[scanTranscript] non-JSON first attempt, retrying. Preview:', clean.slice(0, 200))
  await new Promise(res => setTimeout(res, 5000))

  const retryMessage = baseUserMessage
    + `\n\nIMPORTANT: Your previous response was not valid JSON. `
    + `You must start your response with [ immediately. No other text allowed.`

  try {
    clean = await callClaude(retryMessage)
  } catch (retryErr) {
    onLog(`⚠ Scan failed after retry — ${retryErr.message || 'unknown error'}. 0 refs saved.`, 'warn')
    const empty = []
    empty.nonJson = true
    empty.rawPreview = retryErr.message || ''
    return empty
  }

  if (clean[0] === '[' || clean[0] === '{') {
    try { return normalize(JSON.parse(clean)) } catch (parseErr) {
      onLog(`⚠ Scan failed after retry — 0 refs saved. Claude said: ${clean.slice(0, 200)}`, 'warn')
      // eslint-disable-next-line no-console
      console.warn('[scanTranscript] retry JSON.parse failed:', parseErr.message, 'Preview:', clean.slice(0, 200))
      const empty = []
      empty.nonJson = true
      empty.rawPreview = clean.slice(0, 200)
      empty.parseError = parseErr.message
      return empty
    }
  }

  // Both attempts produced non-JSON output.
  onLog(`⚠ Scan failed after retry — 0 refs saved. Claude said: ${clean.slice(0, 200)}`, 'warn')
  const empty = []
  empty.nonJson = true
  empty.rawPreview = clean.slice(0, 200)
  return empty
}

// ── AI SUMMARY ────────────────────────────────────────────────────────────
// Warm, congregation-facing 2-3 paragraph summary of a sermon. Best-effort:
// returns null on any failure so it never blocks the scan/save pipeline.
export const SUMMARY_SYSTEM_PROMPT = `You are summarising a church sermon for a congregation member. Write in a warm, accessible tone — not academic. 2-3 paragraphs. Focus on: the main Bible passage, the preacher's key argument, and practical application for daily life. Do not start with 'This sermon...'. Start with the key insight or theme.`

export async function generateSermonSummary(transcript, meta = {}) {
  try {
    const excerpt = String(transcript || '').split(/\s+/).slice(0, 3000).join(' ')
    if (!excerpt.trim()) return null
    const userMessage =
      `Sermon title: ${meta.title || 'Untitled'}\n` +
      `Preacher: ${meta.preacher || 'Unknown'}\n` +
      `Church: ${meta.church_name || ''}\n` +
      `Date: ${meta.date || ''}\n` +
      `Transcript excerpt (first 3000 words): ${excerpt}`
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 1024,
        system:     SUMMARY_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userMessage }],
      },
    })
    if (error) return null
    const text = (data?.content?.[0]?.text || '').trim()
    return text || null
  } catch {
    return null
  }
}

// ── DISPLAY TITLE ─────────────────────────────────────────────────────────
// Short (2-5 word) member-facing title, replacing the raw YouTube video
// title everywhere in the app (Sermons tab, This Week, sermon detail) — see
// displayTitleOf() in src/lib/sermonSeries.js for the client-side fallback
// chain. Separate from generateSermonSummary (not changing that function's
// contract) — same best-effort-null-on-failure pattern, called right after
// a summary is generated so it rides the same upload/backfill flow.
export const DISPLAY_TITLE_SYSTEM_PROMPT = `You write short, warm titles for church sermons shown to a congregation in an app. Given a sermon's raw title, Bible passage, and a summary of the sermon, respond with ONLY a short display title of 2-5 words — a topic or theme, not a restatement of the passage reference, no quotation marks, no trailing period. For example: "Disciple as a discipler", "Finding living water", "Walking in the light".`

export async function generateDisplayTitle(summary, meta = {}) {
  try {
    const summaryText = String(summary || '').trim()
    if (!summaryText) return null
    const userMessage =
      `Raw title: ${meta.title || 'Untitled'}\n` +
      `Passage: ${meta.passage || ''}\n` +
      `Summary: ${summaryText}`
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 40,
        system:     DISPLAY_TITLE_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userMessage }],
      },
    })
    if (error) return null
    const text = (data?.content?.[0]?.text || '').trim().replace(/^["']|["']$/g, '').replace(/[.!]+$/, '')
    return text || null
  } catch {
    return null
  }
}

// ── THE BIG IDEA ─────────────────────────────────────────────────────────
// docs/FIX-SERMON-CLIPS.md §3 — a single, generated one-liner (≤15 words)
// for the sermon sheet's Summary tab. Same best-effort-null-on-failure
// pattern as generateDisplayTitle, called right alongside it so it rides
// the same upload/backfill flow without changing generateSermonSummary's
// own contract.
export const BIG_IDEA_SYSTEM_PROMPT = `You write a single one-line takeaway for a church sermon, shown above its summary in an app. Given a sermon's raw title and a summary of the sermon, respond with ONLY one complete sentence of 15 words or fewer capturing the sermon's central point — not a restatement of the passage reference, no quotation marks. End it with a full stop like a normal sentence. For example: Real beauty is a heart that trusts God. / God meets us even when we feel unseen.`

export async function generateBigIdea(summary, meta = {}) {
  try {
    const summaryText = String(summary || '').trim()
    if (!summaryText) return null
    const userMessage = `Raw title: ${meta.title || 'Untitled'}\nSummary: ${summaryText}`
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 60,
        system:     BIG_IDEA_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userMessage }],
      },
    })
    if (error) return null
    // Unlike generateDisplayTitle, this IS a full sentence (the design shows
    // it with its own trailing period) — only strip stray wrapping quotes.
    const text = (data?.content?.[0]?.text || '').trim().replace(/^["']|["']$/g, '')
    return text || null
  } catch {
    return null
  }
}

// ── ENCOURAGEMENT SUMMARY + TITLE ─────────────────────────────────────────
// docs/FIX-ENCOURAGEMENT-UPLOAD.md — turns whatever an admin pastes (a full
// transcript excerpt, or just a few sentences) into a short, Community-ready
// summary + title, plus a classification flag so the upload tool can warn
// when what's actually been pasted is sermon teaching, not a congregation
// encouragement. Lives here (not a new file) to match this module's existing
// "claude-proxy helper" convention (generateSermonSummary/generateDisplayTitle).
export const ENCOURAGEMENT_SUMMARY_SYSTEM_PROMPT = `You are preparing a congregation "encouragement" (a word, testimony or reflection shared in a church service — not the preacher's main sermon) for display in a church app. You will be given raw text an admin pasted in, which may be a short note or a long transcript excerpt.

Respond with a JSON object only, no other text, starting with { immediately:
{
  "title": "a short title, 2-6 words, a theme or phrase from the content — never a truncated sentence, never generic like 'Encouragement', no quotation marks",
  "summary": "one or two warm, congregation-facing sentences capturing the heart of it, maximum 400 characters, written in third person past tense",
  "isLikelySermon": true or false — true only if this reads like the preacher's own sermon teaching (an exposition of a passage, a sermon point) rather than a congregation member's word, testimony or brief encouragement
}`

export async function generateEncouragementSummary(text) {
  try {
    const excerpt = String(text || '').trim()
    if (!excerpt) return null
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 400,
        system:     ENCOURAGEMENT_SUMMARY_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: excerpt.split(/\s+/).slice(0, 3000).join(' ') }],
      },
    })
    if (error) return null
    const raw = (data?.content?.[0]?.text || '').trim().replace(/^```[a-z]*\n?/i, '').replace(/\n?```$/i, '').trim()
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return {
      title:          String(parsed?.title || '').trim() || null,
      summary:        String(parsed?.summary || '').trim().slice(0, 400) || null,
      isLikelySermon: parsed?.isLikelySermon === true,
    }
  } catch {
    return null
  }
}

// ── MAIN PASSAGE CLASSIFICATION ───────────────────────────────────────────
// docs/FIX-SERMON-CLIPS.md §4.1 — no sermon_refs.kind = 'main_passage' rows
// existed before this fix (the chapter-majority heuristic in
// supabase-schema-sermon-passages.sql found none in practice). Given a
// sermon's reference list (ref, timestamp, note — no transcript, so this is
// cheap), asks which one is the sermon's main text. Used both by the
// one-off AdminPanel backfill tool and by new-sermon processing going
// forward, so every sermon ends up with exactly one main_passage.
export const MAIN_PASSAGE_SYSTEM_PROMPT = `You are given the list of Bible references found in one church sermon, each with an id, its timestamp in the video, and a short note describing what was said about it. Identify which ONE reference is the sermon's main passage — the primary text being preached on (usually where the longest, most central teaching happens), not a supporting cross-reference or a brief mention in passing. Respond with ONLY that reference's id, exactly as given, nothing else — no explanation, no punctuation. If no reference is clearly a main passage, respond with the single word: none`

export async function classifyMainPassage(sermonTitle, refs) {
  try {
    const list = (refs || []).filter(r => r?.id)
    if (list.length === 0) return null
    if (list.length === 1) return list[0]
    const lines = list.map(r => {
      const range = r.end_verse != null && r.end_verse !== r.verse ? `${r.verse}-${r.end_verse}` : r.verse
      const ts = r.timestamp_s != null ? `${Math.floor(r.timestamp_s / 60)}:${String(Math.floor(r.timestamp_s % 60)).padStart(2, '0')}` : '—'
      return `${r.id} | ${r.book} ${r.chapter}:${range} | ${ts} | ${(r.notes || '').slice(0, 200)}`
    }).join('\n')
    const userMessage = `Sermon: ${sermonTitle || 'Untitled'}\n\nReferences (id | reference | timestamp | note):\n${lines}`
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 60,
        system:     MAIN_PASSAGE_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userMessage }],
      },
    })
    if (error) return null
    const text = (data?.content?.[0]?.text || '').trim()
    if (!text || /^none$/i.test(text)) return null
    return list.find(r => String(r.id) === text) || null
  } catch {
    return null
  }
}

// ── 5-DAY READING PLAN ───────────────────────────────────────────────────
// Turns one sermon's transcript + its already-extracted sermon_refs into a
// 5-day devotional (passage, devotional, reflection question, and — where a
// ref matches — a timestamp-linked "watch this moment" YouTube URL, built the
// same way FullSermonSheet.jsx's youtubeUrl() helper does).
export const READING_PLAN_SYSTEM_PROMPT = `You are creating a 5-day Bible reading plan from a single church sermon. You will receive the sermon's title, preacher and date, a list of Bible references the preacher used in the sermon (each with a timestamp and a short note on how it was used), and a transcript excerpt.

Create exactly 5 days. Each day must have:
- day_number: integer 1-5
- title: a short day title, 2-5 words, a theme or moment from that day's passage/devotional (e.g. "Lose your life to find it") — never a restatement of the reference, no quotation marks.
- passage_ref: a single Bible reference or short range (e.g. "Romans 8:1-11") the member should read that day. Prefer references actually used in the sermon; order the days to build toward the sermon's main point.
- devotional: 150-250 words, warm and pastoral (not academic), written in second person, connecting the day's passage to the sermon's message and to daily life. Do not start with "In this passage...".
- reflection: ONE thoughtful, open-ended reflection question tied to the day's passage and devotional.
- sermon_ref_match: the verse_ref string from the supplied reference list that this day's passage is drawn from, copied EXACTLY as given (so the app can link back to that moment in the sermon). Use null if no supplied reference matches this day's passage.

Never use em dashes (—) anywhere in your output. Use a comma, period, or parentheses instead.

Respond with a JSON object only, no other text, starting with { immediately:
{
  "title": "short plan title, e.g. 'Finding Freedom in Romans 8'",
  "summary": "one sentence describing the plan",
  "days": [
    { "day_number": 1, "title": "...", "passage_ref": "...", "devotional": "...", "reflection": "...", "sermon_ref_match": "..." }
  ]
}`

function readingPlanSecondsToLabel(s) {
  const n = Number(s)
  if (!Number.isFinite(n) || n < 0) return ''
  const mins = Math.floor(n / 60)
  const secs = Math.floor(n % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Em dashes should never appear in reading plan text (title, summary,
// devotional, reflection) - a hard guarantee, not just a prompt instruction,
// since Claude's output can't be fully trusted and an admin's manual edit
// could introduce one too. Applied both at generation and at save time.
function stripEmDash(text) {
  return String(text ?? '').replace(/\s*[—–]\s*/g, ', ').replace(/,(\s*,)+/g, ',')
}

// Build the same "?t=<secs>s" YouTube link FullSermonSheet.jsx's youtubeUrl()
// helper builds, without importing that component (it's UI, this is a hook).
function readingPlanWatchUrl(sermon, timestampS) {
  const id = sermon?.youtube_id
  const base = id ? `https://www.youtube.com/watch?v=${id}` : (sermon?.youtube_url || '')
  if (!base) return null
  const t = Number(timestampS)
  if (!Number.isFinite(t) || t <= 0) return base
  return `${base}${base.includes('?') ? '&' : '?'}t=${Math.floor(t)}s`
}

// Generate the 5 days. `refs` is the sermon's sermon_refs rows (verse_ref,
// timestamp, timestamp_s, notes) — the caller (FullSermonSheet) already has
// these loaded, no extra query here. Throws on any validation failure — a
// broken plan must never reach the review screen (unlike scanTranscript,
// where "0 refs" is an acceptable result).
export async function generateReadingPlan(sermon, refs) {
  const refLines = (refs || [])
    .map(r => `${r.verse_ref} | ${r.timestamp || ''} | ${r.notes || ''}`)
    .join('\n')
  const excerpt = String(sermon?.transcript || '').split(/\s+/).slice(0, 4000).join(' ')

  const userMessage =
    `Sermon: "${sermon?.title || ''}" | Preacher: ${sermon?.preacher || 'Unknown'} | Date: ${sermon?.date || ''}\n\n` +
    `Bible references used in this sermon (verse_ref | timestamp | note):\n${refLines || '(none extracted)'}\n\n` +
    `Transcript excerpt:\n${excerpt}\n\n` +
    `Respond with the JSON object only, starting with { immediately. No explanation.`

  async function callClaude(message) {
    const { data, error } = await supabase.functions.invoke('claude-proxy', {
      body: {
        model:      'claude-sonnet-4-6',
        max_tokens: 3000,
        system:     READING_PLAN_SYSTEM_PROMPT,
        messages: [{ role: 'user', content: message }],
      },
    })
    if (error) {
      const upstream = data?.error?.message || data?.message || ''
      throw new Error(upstream || error.message || 'claude-proxy failed')
    }
    if (data?.error) throw new Error(data.error.message || JSON.stringify(data.error))
    const text = data?.content?.[0]?.text || ''
    if (!text) throw new Error('Empty response from Claude')
    return text.replace(/^```[a-z]*\n?/i, '').replace(/\n?```$/i, '').trim()
  }

  function parseAndValidate(raw) {
    let parsed
    try { parsed = JSON.parse(raw) } catch (e) {
      throw new Error('Claude returned non-JSON: ' + raw.slice(0, 200))
    }
    const days = Array.isArray(parsed?.days) ? parsed.days : []
    if (days.length !== 5) {
      throw new Error(`Claude returned ${days.length} days, expected 5 — try again.`)
    }
    const normalized = days.map((d, i) => {
      const title       = stripEmDash(d?.title).trim() || null
      const passage_ref = stripEmDash(d?.passage_ref).trim()
      const devotional  = stripEmDash(d?.devotional).trim()
      const reflection  = stripEmDash(d?.reflection).trim()
      if (!passage_ref || !devotional || !reflection) {
        throw new Error(`Day ${i + 1} is missing a passage, devotional, or reflection question — try again.`)
      }
      const match = (refs || []).find(r =>
        String(r.verse_ref || '').toLowerCase() === String(d?.sermon_ref_match || '').toLowerCase()
      )
      const watch_url   = match ? readingPlanWatchUrl(sermon, match.timestamp_s) : null
      const watch_label = match ? `Watch this moment (${readingPlanSecondsToLabel(match.timestamp_s)})` : null
      return {
        day_number:    i + 1,
        title,
        passage_ref,
        devotional,
        reflection,
        sermon_ref_id: match?.id || null,
        watch_url,
        watch_label,
      }
    })
    return {
      title:   stripEmDash(parsed?.title).trim() || `Reading plan: ${sermon?.title || ''}`,
      summary: stripEmDash(parsed?.summary).trim() || null,
      days:    normalized,
    }
  }

  let raw = await callClaude(userMessage)
  try {
    return parseAndValidate(raw)
  } catch (firstErr) {
    // One retry with a stronger nudge, same pattern as scanTranscript.
    await new Promise(res => setTimeout(res, 3000))
    raw = await callClaude(userMessage + `\n\nIMPORTANT: Your previous response was invalid (${firstErr.message}). Return ONLY the JSON object, starting with { immediately.`)
    return parseAndValidate(raw)
  }
}

// ── READING PLAN PERSISTENCE ─────────────────────────────────────────────
// Save (or re-save) a draft: upsert the plan row, then replace its days
// wholesale — same delete-then-insert pattern useSermonAdmin.saveSermon()
// uses for sermon_refs, so a re-generate/re-edit never doubles rows.
export async function saveReadingPlanDraft({ planId, sermonId, churchId, userId, title, summary, days }) {
  // Sanitize again on save - the admin's review/edit screen is free-text, so
  // a pasted or typed em dash needs to be caught here too, not just at
  // generation time.
  const planRow = {
    id:           planId || undefined,
    sermon_id:    sermonId,
    church_id:    churchId,
    title:        stripEmDash(title),
    summary:      stripEmDash(summary),
    status:       'draft',
    generated_by: userId,
    updated_at:   new Date().toISOString(),
  }
  const { data: plan, error: pErr } = await supabase
    .from('reading_plans').upsert(planRow, { onConflict: 'sermon_id' }).select().single()
  if (pErr) throw pErr

  await supabase.from('reading_plan_days').delete().eq('plan_id', plan.id)

  if (days?.length) {
    const { error: dErr } = await supabase.from('reading_plan_days').insert(
      days.map(d => ({
        plan_id:       plan.id,
        day_number:    d.day_number,
        title:         d.title ? stripEmDash(d.title).trim() : null,
        passage_ref:   stripEmDash(d.passage_ref),
        devotional:    stripEmDash(d.devotional),
        reflection:    stripEmDash(d.reflection),
        watch_url:     d.watch_url || null,
        watch_label:   d.watch_label || null,
        sermon_ref_id: d.sermon_ref_id || null,
      }))
    )
    if (dErr) throw dErr
  }

  return plan
}

export async function approveReadingPlan(planId, userId) {
  const { error } = await supabase.from('reading_plans').update({
    status: 'approved', reviewed_by: userId, reviewed_at: new Date().toISOString(),
  }).eq('id', planId)
  if (error) throw error
}

export async function publishReadingPlan(planId, userId) {
  const now = new Date().toISOString()
  const { error } = await supabase.from('reading_plans').update({
    status: 'published', reviewed_by: userId, reviewed_at: now, published_at: now,
  }).eq('id', planId)
  if (error) throw error
}

export async function unpublishReadingPlan(planId) {
  const { error } = await supabase.from('reading_plans').update({ status: 'approved' }).eq('id', planId)
  if (error) throw error
}
